import React, { useState, useEffect, useCallback } from "react";
import {
  auth, ouvirAuth, loginComGoogle, loginComEmail, logout,
  buscarPerfil, salvarPerfil,
  ouvirFeed, criarPost, apagarPost, reagirPost,
  ouvirCircuito, criarPostCircuito, aprovarPostCircuito, apagarPostCircuito,
  ouvirComunidades, criarComunidade, entrarComunidade, sairComunidade, verificarMembrosPorPerfil,
  ouvirTopicos, criarTopico,
  ouvirRecados, enviarRecado, apagarRecado,
  buscarUsoNeura, registrarUsoNeura,
  db, doc, updateDoc, addDoc, collection, onSnapshot,
  query, where, orderBy, serverTimestamp,
} from "./services/firebase";

// ─── TOKENS ───────────────────────────────────────────────────────────────────
const T = {
  roxo: "#5848d0", roxoLight: "#EBE8FF", roxoMid: "#7B6EE8",
  roxoDark: "#3d33a0",
  laranja: "#FF8C00", laranjaLight: "#FFF4E5",
  bg: "#F0EEFF", white: "#ffffff",
  gray50: "#F8F7FF", gray100: "#EDEDF5", gray200: "#E0DDEE",
  gray300: "#C5C3D8", gray500: "#888099", gray700: "#4A4462",
  green: "#22C55E", red: "#EF4444",
};

// Imagens de capa para comunidades (sem custo, Unsplash source)
const CAPAS = [
  "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=800&q=80",
  "https://images.unsplash.com/photo-1544027993-37dbfe43562a?w=800&q=80",
  "https://images.unsplash.com/photo-1491013516836-7db643ee125a?w=800&q=80",
  "https://images.unsplash.com/photo-1516627145497-ae6968895b74?w=800&q=80",
  "https://images.unsplash.com/photo-1511632765486-a01980e01a18?w=800&q=80",
  "https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?w=800&q=80",
  "https://images.unsplash.com/photo-1476703993599-0035a21b17a9?w=800&q=80",
  "https://images.unsplash.com/photo-1560969184-10fe8719e047?w=800&q=80",
  "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&q=80",
  "https://images.unsplash.com/photo-1588072432836-e10032774350?w=800&q=80",
  "https://images.unsplash.com/photo-1484863137850-59afcfe05386?w=800&q=80",
  "https://images.unsplash.com/photo-1609220136736-443140cffec6?w=800&q=80",
];

const EMOJIS_COMUNIDADE = [
  "🌈","🧩","⚡","💜","🧠","❤️","🌟","🤝","📚","🎯","🌱","🏡",
  "🦋","🎨","🎭","🎪","🌊","🔥","💡","🏆","🌸","🦄",
];

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const ago = (ts) => {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const diff = (Date.now() - d) / 1000;
  if (diff < 60) return "agora mesmo";
  if (diff < 3600) return `${Math.floor(diff / 60)}min atrás`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h atrás`;
  return d.toLocaleDateString("pt-BR");
};

const useIsMobile = () => {
  const [mobile, setMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const h = () => setMobile(window.innerWidth < 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return mobile;
};

// ─── BASE COMPONENTS ──────────────────────────────────────────────────────────
const Avatar = ({ src, size = 40, border, emoji }) => (
  <div style={{
    width: size, height: size, borderRadius: "50%", overflow: "hidden", flexShrink: 0,
    border: border ? `3px solid ${T.laranja}` : undefined,
    background: T.roxoLight, display: "flex", alignItems: "center",
    justifyContent: "center", fontSize: size * 0.45,
  }}>
    {emoji
      ? emoji
      : <img src={src || `https://api.dicebear.com/7.x/adventurer/svg?seed=u${size}`} alt=""
          style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
  </div>
);

const Card = ({ children, style }) => (
  <div style={{
    background: T.white, borderRadius: 24, padding: 20,
    boxShadow: "0 4px 24px rgba(88,72,208,0.08)",
    marginBottom: 16, ...style,
  }}>{children}</div>
);

const Btn = ({ children, onClick, variant = "primary", style, disabled, small }) => {
  const variants = {
    primary: { background: T.roxo, color: "#fff" },
    secondary: { background: T.roxoLight, color: T.roxo },
    ghost: { background: "transparent", color: T.roxo, border: `2px solid ${T.roxo}` },
    orange: { background: T.laranja, color: "#fff" },
    danger: { background: "#FEE2E2", color: T.red },
    warn: { background: "#FEF9C3", color: "#854d0e" },
  };
  return (
    <button onClick={onClick} disabled={disabled} style={{
      border: "none", borderRadius: small ? 10 : 14, fontWeight: 700,
      fontFamily: "inherit", cursor: disabled ? "not-allowed" : "pointer",
      padding: small ? "6px 14px" : "11px 22px",
      fontSize: small ? "0.78rem" : "0.9rem",
      transition: "all 0.2s", opacity: disabled ? 0.55 : 1,
      ...variants[variant], ...style,
    }}>{children}</button>
  );
};

const Input = ({ style, ...props }) => (
  <input style={{
    width: "100%", padding: "12px 14px", borderRadius: 12,
    border: `2px solid ${T.gray100}`, fontFamily: "inherit",
    boxSizing: "border-box", outline: "none", fontSize: "0.95rem",
    color: T.gray700, background: "#fff", ...style,
  }} {...props} />
);

const Textarea = ({ style, ...props }) => (
  <textarea style={{
    width: "100%", padding: "12px 14px", borderRadius: 14,
    border: `2px solid ${T.gray100}`, resize: "none",
    fontFamily: "inherit", fontSize: "0.95rem",
    boxSizing: "border-box", outline: "none", color: T.gray700, ...style,
  }} {...props} />
);

const Tag = ({ children, color = T.roxo, bg = T.roxoLight }) => (
  <span style={{
    background: bg, color, fontSize: "0.7rem", fontWeight: 700,
    padding: "3px 10px", borderRadius: 20, display: "inline-block",
  }}>{children}</span>
);

const Divider = () => <div style={{ height: 1, background: T.gray100, margin: "12px 0" }} />;

// ─── BOTTOM NAV (mobile) ──────────────────────────────────────────────────────
const BottomNav = ({ page, onNav }) => (
  <nav style={{
    position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 200,
    background: T.white, boxShadow: "0 -4px 20px rgba(88,72,208,0.1)",
    display: "flex", justifyContent: "space-around", padding: "10px 0 16px",
  }}>
    {[
      ["dashboard", "🏠", "Home"],
      ["comunidades", "🤝", "Grupos"],
      ["amigos", "👥", "Amigos"],
      ["recados", "✉️", "Recados"],
      ["perfil", "⚙️", "Perfil"],
    ].map(([id, icon, label]) => (
      <button key={id} onClick={() => onNav(id)} style={{
        background: "none", border: "none", cursor: "pointer",
        display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
        color: page === id ? T.roxo : T.gray300,
        fontFamily: "inherit", fontSize: "0.65rem", fontWeight: page === id ? 700 : 500,
        padding: "4px 12px", borderRadius: 12,
        background: page === id ? T.roxoLight : "none",
      }}>
        <span style={{ fontSize: "1.3rem" }}>{icon}</span>
        {label}
      </button>
    ))}
  </nav>
);

// ─── HEADER ───────────────────────────────────────────────────────────────────
const Header = ({ user, onNav, page, onLogout, kidsMode }) => {
  const mobile = useIsMobile();
  return (
    <header style={{
      position: "sticky", top: 0, zIndex: 100,
      background: kidsMode ? "linear-gradient(135deg, #5848d0, #FF8C00)" : T.white,
      boxShadow: "0 2px 16px rgba(88,72,208,0.09)",
      padding: mobile ? "12px 5%" : "10px 5%",
      display: "flex", alignItems: "center", justifyContent: "space-between",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: mobile ? 12 : 32 }}>
        <div onClick={() => onNav(kidsMode ? "kids" : "dashboard")} style={{
          fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800,
          fontSize: mobile ? "1.2rem" : "1.4rem",
          color: kidsMode ? "#fff" : T.roxo, cursor: "pointer",
        }}>
          {kidsMode ? "⚡ Circuito" : "NeurUp"}
        </div>
        {!kidsMode && !mobile && (
          <nav style={{ display: "flex", gap: 4 }}>
            {[["dashboard","🏠 Home"],["comunidades","🤝 Comunidades"],["amigos","👥 Amigos"],["recados","✉️ Recados"]].map(([id, label]) => (
              <button key={id} onClick={() => onNav(id)} style={{
                background: page === id ? T.roxoLight : "transparent",
                color: page === id ? T.roxo : T.gray500,
                border: "none", padding: "7px 14px", borderRadius: 12,
                fontWeight: page === id ? 700 : 500, cursor: "pointer",
                fontSize: "0.82rem", fontFamily: "inherit", transition: "0.2s",
              }}>{label}</button>
            ))}
          </nav>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {kidsMode && (
          <button onClick={() => onNav("dashboard")} style={{
            background: "rgba(255,255,255,0.2)", color: "#fff", border: "none",
            padding: "7px 12px", borderRadius: 12, cursor: "pointer",
            fontFamily: "inherit", fontWeight: 700, fontSize: "0.82rem",
          }}>← Família</button>
        )}
        <Avatar src={user.photoURL} size={34} />
        <button onClick={onLogout} style={{
          background: "none", border: "none",
          color: kidsMode ? "rgba(255,255,255,0.8)" : T.red,
          fontWeight: 700, cursor: "pointer", fontSize: "0.72rem",
        }}>SAIR</button>
      </div>
    </header>
  );
};


// ─── EDITOR RICO (estilo Orkut) ───────────────────────────────────────────────
const CORES = [
  "#000000","#5848d0","#FF8C00","#EF4444","#22C55E",
  "#3B82F6","#EC4899","#F59E0B","#8B5CF6","#06B6D4",
  "#ffffff","#888099",
];

const RichEditor = ({ value, onChange, placeholder, minHeight = 90 }) => {
  const editorRef = React.useRef(null);
  const [showCores, setShowCores] = useState(false);
  const [showImagem, setShowImagem] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [urlImagem, setUrlImagem] = useState("");
  const [urlVideo, setUrlVideo] = useState("");

  // Sincroniza o HTML com o valor externo apenas na montagem
  React.useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || "";
    }
  }, []);

  const exec = (cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    onChange(editorRef.current?.innerHTML || "");
  };

  const inserirImagem = () => {
    if (!urlImagem.trim()) return;
    exec("insertHTML", `<img src="${urlImagem}" alt="imagem" style="max-width:100%;border-radius:12px;margin:8px 0;" />`);
    setUrlImagem(""); setShowImagem(false);
  };

  const inserirVideo = () => {
    if (!urlVideo.trim()) return;
    const match = urlVideo.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([-\w]+)/);
    const id = match?.[1];
    if (id) {
      exec("insertHTML", `<div style="position:relative;padding-bottom:56.25%;height:0;margin:8px 0;border-radius:12px;overflow:hidden;"><iframe src="https://www.youtube.com/embed/${id}" style="position:absolute;top:0;left:0;width:100%;height:100%;border:none;" allowfullscreen></iframe></div>`);
    } else {
      exec("insertHTML", `<a href="${urlVideo}" target="_blank" style="color:${T.roxo};font-weight:700;">${urlVideo}</a>`);
    }
    setUrlVideo(""); setShowVideo(false);
  };

  const btnStyle = (active) => ({
    background: active ? T.roxoLight : "transparent",
    border: `1px solid ${T.gray100}`, borderRadius: 8,
    padding: "5px 9px", cursor: "pointer", fontSize: "0.82rem",
    fontFamily: "inherit", color: T.gray700, fontWeight: 700,
    transition: "0.15s",
  });

  return (
    <div style={{ border: `2px solid ${T.gray100}`, borderRadius: 16, overflow: "hidden", background: "#fff" }}>
      {/* Barra de ferramentas */}
      <div style={{
        display: "flex", flexWrap: "wrap", gap: 4, padding: "8px 10px",
        borderBottom: `1px solid ${T.gray100}`, background: T.gray50,
        alignItems: "center",
      }}>
        <button style={btnStyle(false)} onClick={() => exec("bold")} title="Negrito"><b>N</b></button>
        <button style={btnStyle(false)} onClick={() => exec("italic")} title="Itálico"><i>I</i></button>
        <button style={btnStyle(false)} onClick={() => exec("underline")} title="Sublinhado"><u>S</u></button>
        <div style={{ width: 1, height: 20, background: T.gray200, margin: "0 4px" }} />
        <select onChange={e => exec("fontSize", e.target.value)} defaultValue="3"
          style={{ border: `1px solid ${T.gray100}`, borderRadius: 8, padding: "4px 6px", fontFamily: "inherit", fontSize: "0.78rem", background: "#fff", cursor: "pointer" }}>
          <option value="1">Pequeno</option>
          <option value="3">Normal</option>
          <option value="5">Grande</option>
          <option value="7">Enorme</option>
        </select>
        <div style={{ width: 1, height: 20, background: T.gray200, margin: "0 4px" }} />
        {/* Seletor de cor */}
        <div style={{ position: "relative" }}>
          <button style={{ ...btnStyle(showCores), display: "flex", alignItems: "center", gap: 4 }}
            onClick={() => { setShowCores(v => !v); setShowImagem(false); setShowVideo(false); }}>
            🎨 Cor
          </button>
          {showCores && (
            <div style={{
              position: "absolute", top: "110%", left: 0, zIndex: 100,
              background: "#fff", border: `1px solid ${T.gray100}`, borderRadius: 12,
              padding: 8, display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 4,
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            }}>
              {CORES.map(cor => (
                <button key={cor} onClick={() => { exec("foreColor", cor); setShowCores(false); }}
                  style={{ width: 22, height: 22, borderRadius: 6, background: cor, border: cor === "#ffffff" ? `1px solid ${T.gray200}` : "none", cursor: "pointer" }} />
              ))}
            </div>
          )}
        </div>
        <div style={{ width: 1, height: 20, background: T.gray200, margin: "0 4px" }} />
        {/* Imagem */}
        <div style={{ position: "relative" }}>
          <button style={btnStyle(showImagem)}
            onClick={() => { setShowImagem(v => !v); setShowCores(false); setShowVideo(false); }}>
            🖼️ Imagem
          </button>
          {showImagem && (
            <div style={{
              position: "absolute", top: "110%", left: 0, zIndex: 100,
              background: "#fff", border: `1px solid ${T.gray100}`, borderRadius: 12,
              padding: 12, width: 280, boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            }}>
              <input value={urlImagem} onChange={e => setUrlImagem(e.target.value)}
                placeholder="Cole o link da imagem..."
                style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: `1px solid ${T.gray200}`, fontFamily: "inherit", boxSizing: "border-box", marginBottom: 8, outline: "none" }} />
              <Btn small onClick={inserirImagem} disabled={!urlImagem.trim()}>Inserir</Btn>
            </div>
          )}
        </div>
        {/* Vídeo */}
        <div style={{ position: "relative" }}>
          <button style={btnStyle(showVideo)}
            onClick={() => { setShowVideo(v => !v); setShowCores(false); setShowImagem(false); }}>
            🎬 Vídeo
          </button>
          {showVideo && (
            <div style={{
              position: "absolute", top: "110%", left: 0, zIndex: 100,
              background: "#fff", border: `1px solid ${T.gray100}`, borderRadius: 12,
              padding: 12, width: 280, boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            }}>
              <input value={urlVideo} onChange={e => setUrlVideo(e.target.value)}
                placeholder="Cole o link do YouTube..."
                style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: `1px solid ${T.gray200}`, fontFamily: "inherit", boxSizing: "border-box", marginBottom: 8, outline: "none" }} />
              <Btn small onClick={inserirVideo} disabled={!urlVideo.trim()}>Inserir</Btn>
            </div>
          )}
        </div>
      </div>

      {/* Área editável */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={() => onChange(editorRef.current?.innerHTML || "")}
        data-placeholder={placeholder}
        style={{
          minHeight, padding: "12px 14px", outline: "none",
          fontSize: "0.95rem", lineHeight: 1.7, color: T.gray700,
          wordBreak: "break-word",
        }}
      />
      <style>{`
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #888099;
          pointer-events: none;
        }
      `}</style>
    </div>
  );
};

// ─── NEURA FLUTUANTE (para pais) ──────────────────────────────────────────────
const NeuraFlutuante = ({ user, perfil }) => {
  const [aberta, setAberta] = useState(false);
  const [pergunta, setPergunta] = useState("");
  const [conversa, setConversa] = useState([]);
  const [loading, setLoadingN] = useState(false);
  const [usoNeura, setUsoNeura] = useState(0);
  const LIMITE = 10;
  const fimRef = React.useRef(null);

  React.useEffect(() => {
    if (aberta) buscarUsoNeura(user.uid).then(setUsoNeura);
  }, [aberta, user.uid]);

  React.useEffect(() => {
    fimRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversa]);

  const perguntar = async () => {
    if (!pergunta.trim() || usoNeura >= LIMITE) return;
    const p = pergunta;
    setPergunta("");
    setConversa(c => [...c, { role: "user", text: p }]);
    setLoadingN(true);
    try {
      const msgs = [...conversa, { role: "user", text: p }].map(m => ({
        role: m.role, content: m.text,
      }));
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-haiku-4-5", max_tokens: 1000,
          system: `Você é a Neura, assistente especializada em neurodiversidade para famílias brasileiras. Responda com carinho, de forma clara e prática. Perfil atual: ${perfil?.persona || "Responsável"}. Foque em dicas práticas, orientações sobre TEA, TDAH, dislexia, relacionamento familiar e educação inclusiva. Responda sempre em português do Brasil.`,
          messages: msgs,
        }),
      });
      const data = await res.json();
      const txt = data.content?.map(b => b.text || "").join("") || "Ops, tente novamente! 🤔";
      setConversa(c => [...c, { role: "assistant", text: txt }]);
      await registrarUsoNeura(user.uid);
      setUsoNeura(u => u + 1);
    } catch {
      setConversa(c => [...c, { role: "assistant", text: "Estou descansando um pouquinho. Tente em breve! 💤" }]);
    }
    setLoadingN(false);
  };

  return (
    <>
      {/* Botão flutuante */}
      <button onClick={() => setAberta(v => !v)} style={{
        position: "fixed", bottom: 80, right: 20, zIndex: 300,
        width: 56, height: 56, borderRadius: "50%", border: "none",
        background: `linear-gradient(135deg, ${T.roxo}, ${T.laranja})`,
        color: "#fff", fontSize: "1.5rem", cursor: "pointer",
        boxShadow: "0 8px 24px rgba(88,72,208,0.4)",
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "transform 0.2s",
      }}>
        {aberta ? "✕" : "🧠"}
      </button>

      {/* Painel da Neura */}
      {aberta && (
        <div style={{
          position: "fixed", bottom: 150, right: 20, zIndex: 299,
          width: 320, maxHeight: 480,
          background: "#fff", borderRadius: 24,
          boxShadow: "0 16px 48px rgba(88,72,208,0.2)",
          display: "flex", flexDirection: "column", overflow: "hidden",
        }}>
          {/* Header */}
          <div style={{
            background: `linear-gradient(135deg, ${T.roxo}, ${T.laranja})`,
            padding: "14px 16px", color: "#fff",
          }}>
            <div style={{ fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>🧠 Neura</div>
            <div style={{ fontSize: "0.75rem", opacity: 0.85 }}>
              {usoNeura >= LIMITE ? "Limite diário atingido 💤" : `${LIMITE - usoNeura} perguntas restantes hoje`}
            </div>
          </div>

          {/* Conversa */}
          <div style={{ flex: 1, overflowY: "auto", padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
            {conversa.length === 0 && (
              <div style={{ textAlign: "center", color: T.gray500, fontSize: "0.82rem", padding: 20, lineHeight: 1.6 }}>
                Olá! Sou a Neura 💜<br />
                Pode me perguntar sobre neurodiversidade, rotinas, escola, relacionamentos familiares...
              </div>
            )}
            {conversa.map((m, i) => (
              <div key={i} style={{
                maxWidth: "85%", padding: "9px 12px", borderRadius: 14, fontSize: "0.83rem", lineHeight: 1.5,
                alignSelf: m.role === "user" ? "flex-end" : "flex-start",
                background: m.role === "user" ? T.roxo : T.gray50,
                color: m.role === "user" ? "#fff" : T.gray700,
                borderBottomRightRadius: m.role === "user" ? 4 : 14,
                borderBottomLeftRadius: m.role === "assistant" ? 4 : 14,
              }}>{m.text}</div>
            ))}
            {loading && (
              <div style={{ alignSelf: "flex-start", background: T.gray50, padding: "9px 12px", borderRadius: 14, fontSize: "0.82rem", color: T.gray500 }}>
                Pensando... 🤔
              </div>
            )}
            <div ref={fimRef} />
          </div>

          {/* Input */}
          <div style={{ padding: "10px 12px", borderTop: `1px solid ${T.gray100}`, display: "flex", gap: 8 }}>
            <input value={pergunta} onChange={e => setPergunta(e.target.value)}
              onKeyDown={e => e.key === "Enter" && perguntar()}
              placeholder="Pergunte à Neura..." disabled={usoNeura >= LIMITE}
              style={{ flex: 1, padding: "9px 12px", borderRadius: 12, border: `1px solid ${T.gray100}`, fontFamily: "inherit", fontSize: "0.85rem", outline: "none" }} />
            <button onClick={perguntar} disabled={loading || !pergunta.trim() || usoNeura >= LIMITE}
              style={{ background: T.roxo, border: "none", borderRadius: 12, padding: "0 14px", cursor: "pointer", color: "#fff", fontSize: "1rem", opacity: (!pergunta.trim() || usoNeura >= LIMITE) ? 0.5 : 1 }}>
              ➤
            </button>
          </div>
        </div>
      )}
    </>
  );
};

// ─── LOGIN ────────────────────────────────────────────────────────────────────
const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");

  const handleEmail = async () => {
    setLoading(true); setErro("");
    try { await loginComEmail(email, senha); }
    catch { setErro("E-mail ou senha incorretos."); setLoading(false); }
  };
  const handleGoogle = async () => {
    setLoading(true); setErro("");
    try { await loginComGoogle(); }
    catch { setErro("Erro ao entrar com Google."); setLoading(false); }
  };

  return (
    <div style={{
      minHeight: "100vh", background: `linear-gradient(135deg, ${T.roxo} 0%, #8B5CF6 50%, ${T.laranja} 100%)`,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }}>
      <div style={{
        background: T.white, padding: "44px 40px", borderRadius: 36,
        textAlign: "center", boxShadow: "0 32px 80px rgba(0,0,0,0.18)",
        width: "100%", maxWidth: 400,
      }}>
        <div style={{ fontSize: "2.8rem", marginBottom: 8 }}>💜</div>
        <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800, fontSize: "2rem", color: T.roxo, marginBottom: 4 }}>NeurUp</div>
        <p style={{ color: T.gray500, fontSize: "0.88rem", marginBottom: 28, lineHeight: 1.5 }}>
          A comunidade das famílias neurodiversas
        </p>
        {erro && <div style={{ background: "#FEE2E2", color: T.red, padding: "10px 14px", borderRadius: 12, marginBottom: 16, fontSize: "0.85rem" }}>{erro}</div>}
        <Input value={email} onChange={e => setEmail(e.target.value)} placeholder="Seu e-mail" type="email" style={{ marginBottom: 12 }} />
        <Input value={senha} onChange={e => setSenha(e.target.value)} placeholder="Sua senha" type="password"
          onKeyDown={e => e.key === "Enter" && handleEmail()} style={{ marginBottom: 20 }} />
        <Btn onClick={handleEmail} style={{ width: "100%", padding: "14px", fontSize: "1rem", marginBottom: 12 }} disabled={loading}>
          {loading ? "Entrando..." : "Entrar na NeurUp"}
        </Btn>
        <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "14px 0", color: T.gray300, fontSize: "0.8rem" }}>
          <div style={{ flex: 1, height: 1, background: T.gray100 }} /> ou <div style={{ flex: 1, height: 1, background: T.gray100 }} />
        </div>
        <button onClick={handleGoogle} style={{
          width: "100%", padding: "12px", border: `2px solid ${T.gray100}`, borderRadius: 14,
          background: "#fff", cursor: "pointer", fontWeight: 600,
          display: "flex", alignItems: "center", justifyContent: "center",
          gap: 10, fontSize: "0.9rem", fontFamily: "inherit",
        }}>
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" width={18} alt="G" />
          Entrar com Google
        </button>
      </div>
    </div>
  );
};

// ─── POST CARD ────────────────────────────────────────────────────────────────
const PostCard = ({ post, currentUid, onDelete }) => (
  <Card style={{ marginBottom: 14 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
      <Avatar src={post.foto} size={44} />
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, color: T.roxo, fontSize: "0.95rem" }}>{post.autor}</div>
        <div style={{ fontSize: "0.72rem", color: T.gray500 }}>{ago(post.data)}</div>
      </div>
      {post.uid === currentUid && (
        <button onClick={() => onDelete(post.id)} style={{ background: "none", border: "none", cursor: "pointer", color: T.gray300, fontSize: "1rem", padding: 4 }}>🗑️</button>
      )}
    </div>
    <div style={{ margin: 0, color: T.gray700, lineHeight: 1.7, fontSize: "0.95rem" }} dangerouslySetInnerHTML={{ __html: post.texto }} />
    {post.reacoes && (
      <div style={{ display: "flex", gap: 6, marginTop: 14, paddingTop: 12, borderTop: `1px solid ${T.gray100}` }}>
        {Object.entries(post.reacoes).map(([emoji, count]) => (
          <button key={emoji} onClick={() => reagirPost(post.id, emoji)} style={{
            background: T.gray50, border: `1px solid ${T.gray100}`, borderRadius: 20,
            padding: "5px 12px", cursor: "pointer", fontSize: "0.82rem",
            fontFamily: "inherit", display: "flex", alignItems: "center", gap: 5,
            color: T.gray700, fontWeight: 600,
          }}>
            {emoji} <span>{count}</span>
          </button>
        ))}
      </div>
    )}
  </Card>
);

// ─── DASHBOARD ────────────────────────────────────────────────────────────────
const DashboardPage = ({ user, perfil, onNav }) => {
  const [feed, setFeed] = useState([]);
  const [texto, setTexto] = useState("");
  const [circuito, setCircuito] = useState([]);
  const [codigoVisivel, setCodigoVisivel] = useState(false);
  const mobile = useIsMobile();

  useEffect(() => {
    if (!user?.uid) return;
    const u1 = ouvirFeed(setFeed);
    const u2 = ouvirCircuito(user.uid, setCircuito);
    return () => { u1(); u2(); };
  }, [user?.uid]);

  const postar = async () => {
    if (!texto.trim()) return;
    await criarPost(user.uid, perfil?.nome || user.displayName, user.photoURL || "", texto);
    setTexto("");
  };

  const filho = perfil?.filho || {};
  const pendentes = circuito.filter(p => p.status === "pendente");

  const sidebar = (
    <>
      {filho.nome && (
        <Card>
          <p style={{ margin: "0 0 12px", fontWeight: 700, color: T.roxo, fontSize: "0.85rem" }}>🎯 Dependente</p>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "2.2rem", marginBottom: 6 }}>{filho.avatar || "⭐"}</div>
            <div style={{ fontWeight: 700, color: T.gray700, fontSize: "0.95rem" }}>{filho.nome}</div>
            <div style={{ fontSize: "0.78rem", color: T.gray500, marginBottom: 12 }}>{filho.idade} anos</div>
            <Btn variant="orange" style={{ width: "100%", marginBottom: 12, fontSize: "0.82rem", padding: "9px" }} onClick={() => onNav("kids")}>
              Abrir Circuito de {filho.nome}
            </Btn>
            <div style={{ background: T.laranjaLight, border: `1px dashed ${T.laranja}`, borderRadius: 12, padding: 10 }}>
              <div style={{ fontSize: "0.7rem", color: T.gray500, fontWeight: 700, marginBottom: 6 }}>🔑 Código Familiar</div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, background: T.roxo, borderRadius: 8, overflow: "hidden" }}>
                <span style={{ color: "#fff", fontFamily: "monospace", fontSize: "0.95rem", padding: "6px 10px", fontWeight: 700, filter: codigoVisivel ? "none" : "blur(5px)", transition: "0.3s" }}>
                  {user.uid.substring(0, 6).toUpperCase()}
                </span>
                <button onClick={() => setCodigoVisivel(v => !v)} style={{ background: "rgba(0,0,0,0.2)", border: "none", color: "#fff", padding: "7px 10px", cursor: "pointer" }}>
                  {codigoVisivel ? "🙈" : "👁️"}
                </button>
              </div>
            </div>
          </div>
        </Card>
      )}
      <Card>
        <p style={{ margin: "0 0 10px", fontWeight: 700, color: T.roxo, fontSize: "0.85rem" }}>🛡️ Monitor Kids</p>
        {pendentes.length === 0
          ? <p style={{ color: T.gray500, fontSize: "0.82rem", margin: 0 }}>Nenhum post aguardando. ✅</p>
          : pendentes.map(p => (
            <div key={p.id} style={{ background: T.laranjaLight, borderRadius: 10, padding: 10, marginBottom: 8, fontSize: "0.8rem" }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>⏳ {p.autorNome}</div>
              <div style={{ color: T.gray700, marginBottom: 8, lineHeight: 1.5 }}>{p.texto}</div>
              <div style={{ display: "flex", gap: 6 }}>
                <Btn small variant="primary" onClick={() => aprovarPostCircuito(p.id)}>✓ Aprovar</Btn>
                <Btn small variant="danger" onClick={() => apagarPostCircuito(p.id)}>✕ Excluir</Btn>
              </div>
            </div>
          ))
        }
      </Card>
    </>
  );

  return (
    <div style={{ maxWidth: 1300, margin: "0 auto", padding: mobile ? "16px 4%" : "24px 5%", paddingBottom: mobile ? 80 : 24 }}>
      {mobile ? (
        <>
          <Card style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
              <Avatar src={user.photoURL} size={48} border />
              <div>
                <div style={{ fontWeight: 700, color: T.roxo }}>{perfil?.nome || user.displayName}</div>
                <Tag>{perfil?.persona || "Responsável"}</Tag>
              </div>
            </div>
            <RichEditor value={texto} onChange={setTexto} placeholder="Compartilhe uma descoberta..." minHeight={70} />
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
              <Btn onClick={postar} disabled={!texto.trim()} small>Postar</Btn>
            </div>
          </Card>
          {feed.map(p => <PostCard key={p.id} post={p} currentUid={user.uid} onDelete={apagarPost} />)}
        </>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "240px 1fr 240px", gap: 20 }}>
          <aside>
            <Card style={{ textAlign: "center", padding: 22 }}>
              <Avatar src={user.photoURL} size={72} border />
              <div style={{ fontWeight: 700, color: T.roxo, fontSize: "1rem", marginTop: 10 }}>{perfil?.nome || user.displayName}</div>
              <Tag style={{ marginTop: 6 }}>{perfil?.persona || "Responsável"}</Tag>
              <div style={{ marginTop: 18, textAlign: "left" }}>
                {[["🏠","Circuito","dashboard"],["🤝","Comunidades","comunidades"],["👥","Amigos","amigos"],["✉️","Recados","recados"],["⚙️","Perfil","perfil"]].map(([ico, lbl, id]) => (
                  <button key={id} onClick={() => onNav(id)} style={{
                    display: "flex", alignItems: "center", gap: 10, padding: "10px 12px",
                    width: "100%", border: "none", borderRadius: 12, cursor: "pointer",
                    background: id === "dashboard" ? T.roxo : "transparent",
                    color: id === "dashboard" ? "#fff" : T.gray700,
                    fontWeight: id === "dashboard" ? 700 : 500,
                    fontFamily: "inherit", fontSize: "0.88rem", marginBottom: 3, textAlign: "left",
                  }}>
                    <span>{ico}</span>{lbl}
                  </button>
                ))}
              </div>
            </Card>
          </aside>
          <section>
            <Card>
              <div style={{ display: "flex", gap: 12 }}>
                <Avatar src={user.photoURL} size={42} />
                <div style={{ flex: 1 }}>
                  <RichEditor value={texto} onChange={setTexto} placeholder="Compartilhe uma descoberta com o Circuito..." minHeight={80} />
                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                    <Btn onClick={postar} disabled={!texto.trim()}>Postar no Circuito</Btn>
                  </div>
                </div>
              </div>
            </Card>
            {feed.length === 0 && (
              <Card style={{ textAlign: "center", color: T.gray500, padding: 48 }}>
                Nenhum post ainda. Seja a primeira a compartilhar! 💜
              </Card>
            )}
            {feed.map(p => <PostCard key={p.id} post={p} currentUid={user.uid} onDelete={apagarPost} />)}
          </section>
          <aside>{sidebar}</aside>
        </div>
      )}
    </div>
  );
};

// ─── TÓPICO INTERNO ───────────────────────────────────────────────────────────
const TopicoPage = ({ topico, comunidade, user, onVoltar }) => {
  const [respostas, setRespostas] = useState([]);
  const [texto, setTexto] = useState("");

  useEffect(() => {
    if (!topico?.id) return;
    const q = query(
      collection(db, "respostas_topico"),
      where("topicoId", "==", topico.id),
      orderBy("data", "asc")
    );
    const unsub = onSnapshot(q, snap =>
      setRespostas(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
    return unsub;
  }, [topico?.id]);

  const responder = async () => {
    if (!texto.trim()) return;
    await addDoc(collection(db, "respostas_topico"), {
      topicoId: topico.id,
      comunidadeId: comunidade.id,
      uid: user.uid,
      nomeAutor: user.displayName || "Membro",
      fotoAutor: user.photoURL || "",
      texto,
      status: "aprovado",
      data: serverTimestamp(),
    });
    setTexto("");
  };

  const ocultar = async (id, status) => {
    await updateDoc(doc(db, "respostas_topico", id), {
      status: status === "oculto" ? "aprovado" : "oculto",
    });
  };

  const excluir = async (id) => {
    await updateDoc(doc(db, "respostas_topico", id), { status: "excluido" });
  };

  const isAdminComun = comunidade.adminUid === user.uid;
  const isAdminTopico = topico.criadoPor === user.uid;
  const podeModerar = isAdminComun || isAdminTopico;

  const visiveis = respostas.filter(r =>
    r.status !== "excluido" && (r.status !== "oculto" || podeModerar)
  );

  return (
    <div style={{ maxWidth: 760, margin: "0 auto", padding: "24px 5%", paddingBottom: 80 }}>
      <button onClick={onVoltar} style={{ background: "none", border: "none", color: T.roxo, fontWeight: 700, cursor: "pointer", marginBottom: 16, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6 }}>
        ← Voltar à comunidade
      </button>

      <Card style={{ borderLeft: `5px solid ${T.roxo}` }}>
        <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800, color: T.roxo, fontSize: "1.2rem", marginBottom: 6 }}>
          {topico.titulo}
        </div>
        <p style={{ color: T.gray700, lineHeight: 1.7, margin: "0 0 10px" }}>{topico.descricao}</p>
        <div style={{ fontSize: "0.75rem", color: T.gray500 }}>Por {topico.nomeAutor} · {ago(topico.data)}</div>
      </Card>

      <div style={{ marginBottom: 16 }}>
        {visiveis.map(r => (
          <Card key={r.id} style={{
            marginBottom: 10,
            opacity: r.status === "oculto" ? 0.6 : 1,
            border: r.status === "oculto" ? `2px dashed ${T.laranja}` : undefined,
          }}>
            {r.status === "oculto" && (
              <div style={{ background: "#FEF9C3", color: "#854d0e", padding: "6px 12px", borderRadius: 8, fontSize: "0.78rem", fontWeight: 700, marginBottom: 10 }}>
                ⚠️ Conteúdo oculto — visível apenas para moderadores
              </div>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <Avatar src={r.fotoAutor} size={38} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, color: T.roxo, fontSize: "0.88rem" }}>{r.nomeAutor}</div>
                <div style={{ fontSize: "0.7rem", color: T.gray500 }}>{ago(r.data)}</div>
              </div>
              {podeModerar && r.uid !== user.uid && (
                <div style={{ display: "flex", gap: 6 }}>
                  <Btn small variant="warn" onClick={() => ocultar(r.id, r.status)}>
                    {r.status === "oculto" ? "👁️ Restaurar" : "🙈 Ocultar"}
                  </Btn>
                  <Btn small variant="danger" onClick={() => excluir(r.id)}>🗑️</Btn>
                </div>
              )}
              {r.uid === user.uid && (
                <Btn small variant="danger" onClick={() => excluir(r.id)}>🗑️</Btn>
              )}
            </div>
            <div style={{ margin: 0, color: T.gray700, lineHeight: 1.6, fontSize: "0.92rem" }} dangerouslySetInnerHTML={{ __html: r.texto }} />
          </Card>
        ))}
      </div>

      <Card>
        <p style={{ margin: "0 0 10px", fontWeight: 700, color: T.roxo, fontSize: "0.88rem" }}>✍️ Sua resposta</p>
        <RichEditor value={texto} onChange={setTexto} placeholder="Escreva sua resposta com carinho..." minHeight={80} />
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
          <Btn onClick={responder} disabled={!texto.trim()}>Responder</Btn>
        </div>
      </Card>
    </div>
  );
};

// ─── COMUNIDADE INTERNA ───────────────────────────────────────────────────────
const ComunidadeInterna = ({ comunidade, user, onVoltar, onEditarCapa }) => {
  const [topicos, setTopicos] = useState([]);
  const [topicoAtivo, setTopicoAtivo] = useState(null);
  const [novoTopico, setNovoTopico] = useState(false);
  const [form, setForm] = useState({ titulo: "", descricao: "" });
  const [editandoCapa, setEditandoCapa] = useState(false);
  const [editandoEmoji, setEditandoEmoji] = useState(false);

  const isAdmin = comunidade.adminUid === user.uid;

  useEffect(() => {
    const unsub = ouvirTopicos(comunidade.id, (lista) =>
      setTopicos(lista.filter(t => t.status !== "excluido"))
    );
    return unsub;
  }, [comunidade.id]);

  const criar = async () => {
    if (!form.titulo.trim()) return;
    await criarTopico(user.uid, comunidade.id, form.titulo, form.descricao);

    setNovoTopico(false);
    setForm({ titulo: "", descricao: "" });
  };

  const ocultarTopico = async (id, status) => {
    await updateDoc(doc(db, "topicos", id), {
      status: status === "oculto" ? "aprovado" : "oculto",
    });
  };

  const excluirTopico = async (id) => {
    await updateDoc(doc(db, "topicos", id), { status: "excluido" });
  };

  const atualizarCapa = async (url) => {
    await updateDoc(doc(db, "comunidades", comunidade.id), { capa: url });
    setEditandoCapa(false);
    comunidade.capa = url;
  };

  const atualizarEmoji = async (emoji) => {
    await updateDoc(doc(db, "comunidades", comunidade.id), { emoji });
    setEditandoEmoji(false);
    comunidade.emoji = emoji;
  };

  if (topicoAtivo) {
    return <TopicoPage topico={topicoAtivo} comunidade={comunidade} user={user} onVoltar={() => setTopicoAtivo(null)} />;
  }

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", paddingBottom: 80 }}>
      {/* Cabeçalho com capa */}
      <div style={{
        position: "relative", height: 220, borderRadius: "0 0 32px 32px", overflow: "hidden",
        background: `linear-gradient(135deg, ${T.roxo}, #8B5CF6)`,
      }}>
        {comunidade.capa && (
          <img src={comunidade.capa} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", position: "absolute", top: 0, left: 0 }} />
        )}
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(to top, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.1) 100%)",
          display: "flex", flexDirection: "column", justifyContent: "flex-end", padding: "20px 28px",
        }}>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 14 }}>
            <div style={{ fontSize: "3.5rem", cursor: isAdmin ? "pointer" : "default" }}
              onClick={() => isAdmin && setEditandoEmoji(v => !v)}>
              {comunidade.emoji || "🌈"}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800, fontSize: "1.6rem", color: "#fff" }}>
                {comunidade.nome}
              </div>
              <div style={{ color: "rgba(255,255,255,0.8)", fontSize: "0.85rem" }}>{comunidade.descricao}</div>
            </div>
            {isAdmin && (
              <button onClick={() => setEditandoCapa(v => !v)} style={{
                background: "rgba(255,255,255,0.2)", border: "none", color: "#fff",
                padding: "8px 14px", borderRadius: 12, cursor: "pointer",
                fontFamily: "inherit", fontWeight: 700, fontSize: "0.8rem",
              }}>
                🖼️ Editar capa
              </button>
            )}
          </div>
        </div>
        <button onClick={onVoltar} style={{
          position: "absolute", top: 16, left: 20,
          background: "rgba(255,255,255,0.2)", border: "none", color: "#fff",
          padding: "7px 14px", borderRadius: 12, cursor: "pointer",
          fontFamily: "inherit", fontWeight: 700, fontSize: "0.82rem",
        }}>← Comunidades</button>
      </div>

      {/* Galeria de emojis */}
      {editandoEmoji && (
        <Card style={{ margin: "16px 5% 0" }}>
          <p style={{ fontWeight: 700, color: T.roxo, margin: "0 0 12px", fontSize: "0.88rem" }}>Escolha um emoji para a comunidade:</p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {EMOJIS_COMUNIDADE.map(e => (
              <button key={e} onClick={() => atualizarEmoji(e)} style={{
                fontSize: "1.6rem", background: T.gray50, border: `2px solid ${T.gray100}`,
                borderRadius: 12, padding: "8px 12px", cursor: "pointer",
              }}>{e}</button>
            ))}
          </div>
        </Card>
      )}

      {/* Galeria de capas */}
      {editandoCapa && (
        <Card style={{ margin: "16px 5% 0" }}>
          <p style={{ fontWeight: 700, color: T.roxo, margin: "0 0 12px", fontSize: "0.88rem" }}>Escolha uma imagem de capa:</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 10 }}>
            {CAPAS.map((url, i) => (
              <div key={i} onClick={() => atualizarCapa(url)} style={{
                height: 80, borderRadius: 12, overflow: "hidden", cursor: "pointer",
                border: comunidade.capa === url ? `3px solid ${T.roxo}` : "3px solid transparent",
                transition: "0.2s",
              }}>
                <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </div>
            ))}
          </div>
        </Card>
      )}

      <div style={{ padding: "20px 5%" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <h2 style={{ margin: 0, color: T.roxo, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: "1.1rem" }}>
            📋 Tópicos ({topicos.length})
          </h2>
          <Btn onClick={() => setNovoTopico(v => !v)} small>+ Novo Tópico</Btn>
        </div>

        {novoTopico && (
          <Card style={{ marginBottom: 16 }}>
            <p style={{ fontWeight: 700, color: T.roxo, margin: "0 0 12px", fontSize: "0.88rem" }}>Criar novo tópico</p>
            <Input value={form.titulo} onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))}
              placeholder="Título do tópico" style={{ marginBottom: 10 }} />
            <Textarea value={form.descricao} onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))}
              placeholder="Descreva o tema do tópico..." style={{ minHeight: 70 }} />
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <Btn onClick={criar} disabled={!form.titulo.trim()}>Criar</Btn>
              <Btn variant="ghost" onClick={() => setNovoTopico(false)}>Cancelar</Btn>
            </div>
          </Card>
        )}

        {topicos.length === 0 && (
          <Card style={{ textAlign: "center", color: T.gray500, padding: 40 }}>
            Nenhum tópico ainda. Crie o primeiro! 💜
          </Card>
        )}

        {topicos.map(t => {
          const isOculto = t.status === "oculto";
          const podeModerarEste = isAdmin || t.criadoPor === user.uid;
          if (isOculto && !podeModerarEste) return null;

          return (
            <Card key={t.id} style={{
              marginBottom: 10, cursor: "pointer",
              border: isOculto ? `2px dashed ${T.laranja}` : `1px solid ${T.gray100}`,
              opacity: isOculto ? 0.7 : 1,
              transition: "box-shadow 0.2s",
            }}>
              {isOculto && (
                <div style={{ background: "#FEF9C3", color: "#854d0e", padding: "5px 10px", borderRadius: 8, fontSize: "0.75rem", fontWeight: 700, marginBottom: 8 }}>
                  ⚠️ Tópico oculto — visível apenas para moderadores
                </div>
              )}
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div onClick={() => setTopicoAtivo(t)} style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, color: T.roxo, fontSize: "0.95rem", marginBottom: 3 }}>{t.titulo}</div>
                  <div style={{ fontSize: "0.8rem", color: T.gray500, lineHeight: 1.4 }}>{t.descricao}</div>
                  <div style={{ fontSize: "0.72rem", color: T.gray300, marginTop: 6 }}>Por {t.nomeAutor || "Membro"} · {ago(t.data)}</div>
                </div>
                {podeModerarEste && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                    <Btn small variant="warn" onClick={e => { e.stopPropagation(); ocultarTopico(t.id, t.status); }}>
                      {isOculto ? "👁️ Restaurar" : "🙈 Ocultar"}
                    </Btn>
                    {isAdmin && (
                      <Btn small variant="danger" onClick={e => { e.stopPropagation(); excluirTopico(t.id); }}>🗑️ Excluir</Btn>
                    )}
                  </div>
                )}
                <span style={{ color: T.gray300, fontSize: "1.2rem" }} onClick={() => setTopicoAtivo(t)}>›</span>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

// ─── COMUNIDADES ──────────────────────────────────────────────────────────────
const ComunidadesPage = ({ user, perfil }) => {
  const [comunidades, setComunidades] = useState([]);
  const [membros, setMembros] = useState(() => verificarMembrosPorPerfil(perfil));
  const [novaComun, setNovaComun] = useState(false);
  const [comunidadeAtiva, setComunidadeAtiva] = useState(null);
  const [form, setForm] = useState({ nome: "", descricao: "", emoji: "🌈" });
  const [emojiPicker, setEmojiPicker] = useState(false);
  const mobile = useIsMobile();

  useEffect(() => {
    if (!user?.uid) return;
    const unsub = ouvirComunidades((lista) => {
      setComunidades(lista);
    });
    return unsub;
  }, [user?.uid]);

  const toggle = async (c, e) => {
    e.stopPropagation();
    if (membros.has(c.id)) {
      await sairComunidade(user.uid, c.id);
      setMembros(s => { const n = new Set(s); n.delete(c.id); return n; });
    } else {
      await entrarComunidade(user.uid, c.id, c.nome || "");
      setMembros(s => new Set([...s, c.id]));
    }
  };

  const criar = async () => {
    if (!form.nome.trim()) return;
    await criarComunidade(user.uid, { ...form, nomeAdmin: user.displayName || "Membro" });
    setNovaComun(false);
    setForm({ nome: "", descricao: "", emoji: "🌈" });
  };

  if (comunidadeAtiva) {
    return <ComunidadeInterna comunidade={comunidadeAtiva} user={user} onVoltar={() => setComunidadeAtiva(null)} />;
  }

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: mobile ? "16px 4%" : "28px 5%", paddingBottom: 80 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <h1 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: T.roxo, margin: 0, fontSize: mobile ? "1.3rem" : "1.6rem" }}>🤝 Comunidades</h1>
        <Btn onClick={() => setNovaComun(v => !v)} small={mobile}>+ Nova</Btn>
      </div>

      {novaComun && (
        <Card style={{ marginBottom: 20 }}>
          <p style={{ fontWeight: 700, color: T.roxo, margin: "0 0 14px" }}>Criar nova comunidade</p>

          {/* Seletor de emoji */}
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: "0.78rem", fontWeight: 700, color: T.gray500, display: "block", marginBottom: 6 }}>EMOJI</label>
            <button onClick={() => setEmojiPicker(v => !v)} style={{
              fontSize: "2rem", background: T.gray50, border: `2px solid ${T.gray100}`,
              borderRadius: 12, padding: "8px 16px", cursor: "pointer",
            }}>{form.emoji}</button>
            {emojiPicker && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8, padding: 12, background: T.gray50, borderRadius: 12 }}>
                {EMOJIS_COMUNIDADE.map(e => (
                  <button key={e} onClick={() => { setForm(f => ({ ...f, emoji: e })); setEmojiPicker(false); }} style={{
                    fontSize: "1.4rem", background: "#fff", border: `1px solid ${T.gray100}`,
                    borderRadius: 8, padding: "6px 10px", cursor: "pointer",
                  }}>{e}</button>
                ))}
              </div>
            )}
          </div>

          <Input value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))}
            placeholder="Nome da comunidade" style={{ marginBottom: 10 }} />
          <Textarea value={form.descricao} onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))}
            placeholder="Descrição da comunidade" style={{ minHeight: 60 }} />
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <Btn onClick={criar} disabled={!form.nome.trim()}>Criar Comunidade</Btn>
            <Btn variant="ghost" onClick={() => setNovaComun(false)}>Cancelar</Btn>
          </div>
        </Card>
      )}

      <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "repeat(auto-fill, minmax(220px, 1fr))", gap: 14 }}>
        {comunidades.map(c => (
          <div key={c.id} style={{
            background: T.white, borderRadius: 20, overflow: "hidden",
            boxShadow: "0 4px 20px rgba(88,72,208,0.08)",
            transition: "transform 0.2s, box-shadow 0.2s",
          }}>
            <div onClick={() => setComunidadeAtiva(c)} style={{
              height: 90, background: c.capa ? `url(${c.capa}) center/cover` : `linear-gradient(135deg, ${T.roxo}, #8B5CF6)`,
              display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
            }}>
              {!c.capa && <span style={{ fontSize: "2.5rem" }}>{c.emoji || "🌈"}</span>}
            </div>
            <div style={{ padding: 16 }}>
              <div onClick={() => setComunidadeAtiva(c)} style={{ cursor: "pointer" }}>
                <div style={{ fontWeight: 800, color: T.roxo, fontSize: "0.95rem", marginBottom: 4, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{c.nome}</div>
                <div style={{ fontSize: "0.78rem", color: T.gray500, marginBottom: 10, lineHeight: 1.4 }}>{c.descricao}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontSize: "0.72rem", color: T.gray300 }}>{(c.membros || 0).toLocaleString("pt-BR")} membros</span>
                <Btn small variant={membros.has(c.id) ? "secondary" : "primary"}
                  onClick={(e) => { e.stopPropagation(); toggle(c, e); }}>
                  {membros.has(c.id) ? "✓ Membro" : "Entrar"}
                </Btn>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── RECADOS ──────────────────────────────────────────────────────────────────
const RecadosPage = ({ user }) => {
  const [recados, setRecados] = useState([]);
  const [respostas, setRespostas] = useState({});
  const mobile = useIsMobile();

  useEffect(() => {
    const unsub = ouvirRecados(user.uid, setRecados);
    return unsub;
  }, [user.uid]);

  const responder = async (paraUid, recadoId) => {
    const msg = respostas[recadoId];
    if (!msg?.trim()) return;
    await enviarRecado(user.uid, paraUid, msg);
    setRespostas(r => ({ ...r, [recadoId]: "" }));
  };

  return (
    <div style={{ maxWidth: 700, margin: "0 auto", padding: mobile ? "16px 4%" : "28px 5%", paddingBottom: 80 }}>
      <h1 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: T.roxo, marginBottom: 20, fontSize: mobile ? "1.3rem" : "1.6rem" }}>✉️ Recados</h1>
      {recados.length === 0 && (
        <Card style={{ textAlign: "center", color: T.gray500, padding: 48 }}>
          Nenhum recado ainda. Quando alguém te escrever aparece aqui! 💜
        </Card>
      )}
      {recados.map(r => (
        <Card key={r.id} style={{ marginBottom: 12 }}>
          <div style={{ display: "flex", gap: 12, marginBottom: 10 }}>
            <Avatar src={r.fotoRemetente} size={42} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, color: T.roxo, fontSize: "0.9rem" }}>{r.nomeRemetente || r.de}</div>
              <div style={{ fontSize: "0.72rem", color: T.gray500 }}>{ago(r.data)}</div>
            </div>
            <button onClick={() => apagarRecado(r.id)} style={{ background: "none", border: "none", cursor: "pointer", color: T.gray300 }}>🗑️</button>
          </div>
          <div style={{ margin: "0 0 12px", color: T.gray700, lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: r.mensagem }} />
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1 }}>
              <RichEditor value={respostas[r.id] || ""} onChange={v => setRespostas(rs => ({ ...rs, [r.id]: v }))}
                placeholder="Responder com carinho..." minHeight={60} />
            </div>
            <Btn onClick={() => responder(r.de, r.id)}>Enviar</Btn>
          </div>
        </Card>
      ))}
    </div>
  );
};

// ─── AMIGOS ───────────────────────────────────────────────────────────────────
const AmigosPage = () => {
  const mobile = useIsMobile();
  return (
    <div style={{ maxWidth: 700, margin: "0 auto", padding: mobile ? "16px 4%" : "28px 5%", paddingBottom: 80 }}>
      <h1 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: T.roxo, marginBottom: 20 }}>👥 Amigos</h1>
      <Card style={{ textAlign: "center", color: T.gray500, padding: 48 }}>
        <div style={{ fontSize: "3rem", marginBottom: 12 }}>🤝</div>
        Em breve você poderá buscar e adicionar amigos aqui! 💜
      </Card>
    </div>
  );
};

// ─── PERFIL ───────────────────────────────────────────────────────────────────
const PerfilPage = ({ user, perfil }) => {
  const [form, setForm] = useState({ nome: perfil?.nome || "", persona: perfil?.persona || "Mãe" });
  const [saved, setSaved] = useState(false);
  const mobile = useIsMobile();

  const save = async () => {
    await salvarPerfil(user.uid, form);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div style={{ maxWidth: 560, margin: "0 auto", padding: mobile ? "16px 4%" : "28px 5%", paddingBottom: 80 }}>
      <h1 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: T.roxo, marginBottom: 20 }}>⚙️ Meu Perfil</h1>
      <Card>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <Avatar src={user.photoURL} size={80} border />
          <div style={{ marginTop: 10, fontWeight: 700, color: T.gray700 }}>{user.email}</div>
        </div>
        <label style={{ fontSize: "0.78rem", fontWeight: 700, color: T.gray500, marginBottom: 6, display: "block" }}>NOME</label>
        <Input value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} style={{ marginBottom: 16 }} />
        <label style={{ fontSize: "0.78rem", fontWeight: 700, color: T.gray500, marginBottom: 6, display: "block" }}>PAPEL NA FAMÍLIA</label>
        <select value={form.persona} onChange={e => setForm(f => ({ ...f, persona: e.target.value }))}
          style={{ width: "100%", padding: "12px 14px", borderRadius: 12, border: `2px solid ${T.gray100}`, fontFamily: "inherit", boxSizing: "border-box", marginBottom: 20, outline: "none", fontSize: "0.95rem" }}>
          {["Mãe","Pai","Avó / Avô","Especialista","Educador(a)"].map(p => <option key={p}>{p}</option>)}
        </select>
        <Btn onClick={save} style={{ width: "100%" }}>{saved ? "✓ Salvo!" : "Salvar Alterações"}</Btn>
      </Card>
    </div>
  );
};

// ─── KIDS ─────────────────────────────────────────────────────────────────────
const KidsPage = ({ user, perfil }) => {
  const filho = perfil?.filho || {};
  const isTeen = parseInt(filho.idade || 0) >= 11;
  const theme = isTeen
    ? { bg: "#121214", card: "#202024", text: "#e1e1e6", roxo: "#8257e5", laranja: "#04d361", radius: 12, border: "#333" }
    : { bg: "#F0EEFF", card: "#fff", text: "#333", roxo: T.roxo, laranja: T.laranja, radius: 24, border: T.gray100 };

  const [feed, setFeed] = useState([]);
  const [texto, setTexto] = useState("");
  const [bilhete, setBilhete] = useState("");
  const [mostrarBilhete, setMostrarBilhete] = useState(false);
  const [pergunta, setPergunta] = useState("");
  const [resposta, setResposta] = useState("");
  const [loadingNeura, setLoadingNeura] = useState(false);
  const [usoNeura, setUsoNeura] = useState(0);
  const LIMITE = 10;
  const mobile = useIsMobile();

  useEffect(() => {
    const unsub = ouvirCircuito(user.uid, posts => setFeed(posts.filter(p => p.status === "aprovado")));
    buscarUsoNeura(user.uid).then(setUsoNeura);
    return unsub;
  }, [user.uid]);

  const postar = async () => {
    if (!texto.trim()) return;
    await criarPostCircuito(user.uid, user.uid, filho?.nomeExibicao || filho?.nome || "Explorador", texto);
    setTexto("");
  };

  const enviarBilhete = async () => {
    if (!bilhete.trim()) return;
    await criarPostCircuito(user.uid, user.uid, "bilhete_mae", bilhete);
    setBilhete(""); setMostrarBilhete(false);
  };

  const perguntarNeura = async () => {
    if (!pergunta.trim() || usoNeura >= LIMITE) return;
    setLoadingNeura(true); setResposta("");
    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-haiku-4-5", max_tokens: 1000,
          system: `Você é a Neura, IA amigável para crianças neurodiversas de ${filho.idade || 8} anos. Responda de forma simples, carinhosa e encorajadora. Use emojis com moderação. Nunca diga nada inadequado. Responda sempre em português do Brasil.`,
          messages: [{ role: "user", content: pergunta }],
        }),
      });
      const data = await res.json();
      setResposta(data.content?.map(b => b.text || "").join("") || "Ops, tente de novo! 🤔");
      await registrarUsoNeura(user.uid);
      setUsoNeura(u => u + 1);
    } catch {
      setResposta("A Neura está descansando. Tente em breve! 💤");
    }
    setPergunta(""); setLoadingNeura(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: theme.bg, transition: "0.5s", paddingBottom: 80 }}>
      <div style={{
        maxWidth: 1100, margin: "0 auto", padding: "20px 5%",
        display: mobile ? "block" : "grid",
        gridTemplateColumns: "220px 1fr 260px", gap: 20,
      }}>
        {/* Perfil kid */}
        {!mobile && (
          <aside>
            <div style={{ background: theme.card, borderRadius: theme.radius, padding: 20, textAlign: "center" }}>
              <div style={{ width: 72, height: 72, borderRadius: "50%", border: `4px solid ${theme.laranja}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "2.2rem", margin: "0 auto 10px", background: theme.bg }}>
                {filho.avatar || "⭐"}
              </div>
              <div style={{ fontWeight: 800, color: theme.roxo, fontSize: "1rem" }}>{filho.nome || "Explorador"}</div>
              <div style={{ fontSize: "0.78rem", opacity: 0.6, marginTop: 4, color: theme.text }}>{filho.bio || ""}</div>
            </div>
          </aside>
        )}

        {/* Feed */}
        <section>
          {mobile && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              <div style={{ fontSize: "2rem" }}>{filho.avatar || "⭐"}</div>
              <div style={{ fontWeight: 800, color: theme.roxo }}>{filho.nome || "Explorador"}</div>
            </div>
          )}
          <div style={{ background: theme.card, borderRadius: theme.radius, padding: 18, marginBottom: 14 }}>
            <p style={{ margin: "0 0 10px", fontWeight: 700, color: theme.roxo, fontSize: "0.88rem" }}>📝 Compartilhar no Circuito</p>
            <RichEditor value={texto} onChange={setTexto}
              placeholder={`O que você quer compartilhar, ${filho?.nomeExibicao || filho?.nome || "Explorador"}?`}
              minHeight={70} />
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
              <button onClick={postar} disabled={!texto.trim()} style={{
                background: theme.laranja, color: isTeen ? "#000" : "#fff",
                border: "none", padding: "9px 18px", borderRadius: 12,
                fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                opacity: texto.trim() ? 1 : 0.5,
              }}>Postar</button>
            </div>
            <p style={{ margin: "8px 0 0", fontSize: "0.72rem", color: theme.roxo, opacity: 0.7 }}>
              ⏳ Aguarda aprovação do responsável para aparecer no Circuito.
            </p>
          </div>

          {/* Neura em mobile */}
          {mobile && (
            <div style={{ background: `linear-gradient(135deg, ${theme.roxo}, ${theme.laranja})`, borderRadius: theme.radius, padding: 18, color: "#fff", marginBottom: 14 }}>
              <div style={{ fontWeight: 800, marginBottom: 4 }}>🧠 Neura</div>
              <div style={{ fontSize: "0.78rem", opacity: 0.85, marginBottom: 10 }}>
                {usoNeura >= LIMITE ? "Limite diário atingido! Volta amanhã 💤" : `${LIMITE - usoNeura} perguntas restantes`}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <input value={pergunta} onChange={e => setPergunta(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && perguntarNeura()}
                  placeholder="Pergunte algo..." disabled={usoNeura >= LIMITE}
                  style={{ flex: 1, padding: "9px 12px", borderRadius: 10, border: "none", background: "rgba(255,255,255,0.15)", color: "#fff", fontFamily: "inherit", outline: "none" }} />
                <button onClick={perguntarNeura} disabled={loadingNeura || !pergunta.trim() || usoNeura >= LIMITE}
                  style={{ background: "rgba(255,255,255,0.25)", border: "none", borderRadius: 10, padding: "0 14px", cursor: "pointer", color: "#fff" }}>
                  {loadingNeura ? "⏳" : "➤"}
                </button>
              </div>
              {resposta && <div style={{ marginTop: 12, background: "rgba(255,255,255,0.12)", borderRadius: 10, padding: 12, fontSize: "0.85rem", lineHeight: 1.6 }}>{resposta}</div>}
            </div>
          )}

          {/* Bilhetinho da mãe */}
          <div style={{ background: theme.card, borderRadius: theme.radius, padding: 16, marginBottom: 14, border: `2px dashed ${theme.laranja}` }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: mostrarBilhete ? 12 : 0 }}>
              <div style={{ fontWeight: 700, color: theme.laranja, fontSize: "0.88rem" }}>
                💌 Deixar um bilhetinho para {filho?.nomeExibicao || filho?.nome}
              </div>
              <button onClick={() => setMostrarBilhete(v => !v)} style={{
                background: theme.laranja, color: "#fff", border: "none",
                borderRadius: 10, padding: "5px 12px", cursor: "pointer",
                fontFamily: "inherit", fontWeight: 700, fontSize: "0.78rem",
              }}>{mostrarBilhete ? "Cancelar" : "+ Escrever"}</button>
            </div>
            {mostrarBilhete && (
              <>
                <RichEditor value={bilhete} onChange={setBilhete}
                  placeholder={`Escreva algo especial para ${filho?.nomeExibicao || filho?.nome}... 💜`}
                  minHeight={70} />
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                  <button onClick={enviarBilhete} disabled={!bilhete.trim()} style={{
                    background: theme.laranja, color: "#fff", border: "none",
                    borderRadius: 10, padding: "8px 16px", cursor: "pointer",
                    fontFamily: "inherit", fontWeight: 700, opacity: bilhete.trim() ? 1 : 0.5,
                  }}>Enviar Bilhete 💌</button>
                </div>
              </>
            )}
          </div>

          {feed.map(p => {
            const isBilhete = p.autorNome === "bilhete_mae";
            return (
              <div key={p.id} style={{
                background: isBilhete ? T.laranjaLight : theme.card,
                borderRadius: theme.radius, padding: 16, marginBottom: 10,
                border: isBilhete ? `2px solid ${T.laranja}` : "none",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <div style={{ fontSize: "1.4rem" }}>{isBilhete ? "💌" : (filho?.avatar || "⭐")}</div>
                  <div>
                    <div style={{ fontWeight: 700, color: isBilhete ? T.laranja : theme.roxo, fontSize: "0.88rem" }}>
                      {isBilhete ? `Bilhete de ${perfil?.nomeExibicao || "Mamãe"}` : p.autorNome}
                    </div>
                    <div style={{ fontSize: "0.68rem", opacity: 0.6, color: theme.text }}>{ago(p.data)}</div>
                  </div>
                </div>
                <div style={{ margin: 0, lineHeight: 1.6, fontSize: "0.9rem", color: isBilhete ? T.gray700 : theme.text }}
                  dangerouslySetInnerHTML={{ __html: p.texto }} />
              </div>
            );
          })}
        </section>

        {/* Neura desktop */}
        {!mobile && (
          <aside>
            <div style={{ background: `linear-gradient(135deg, ${theme.roxo}, ${theme.laranja})`, borderRadius: theme.radius, padding: 20, color: "#fff" }}>
              <h3 style={{ margin: "0 0 4px", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>🧠 Neura</h3>
              <p style={{ margin: "0 0 12px", opacity: 0.85, fontSize: "0.8rem" }}>
                {usoNeura >= LIMITE ? "Limite diário atingido! Volta amanhã 💤" : `${LIMITE - usoNeura} perguntas restantes hoje`}
              </p>
              <div style={{ display: "flex", gap: 8 }}>
                <input value={pergunta} onChange={e => setPergunta(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && perguntarNeura()}
                  placeholder="Pergunte algo..." disabled={usoNeura >= LIMITE}
                  style={{ flex: 1, padding: "10px 12px", borderRadius: 12, border: "none", background: "rgba(255,255,255,0.15)", color: "#fff", fontFamily: "inherit", fontSize: "0.85rem", outline: "none" }} />
                <button onClick={perguntarNeura} disabled={loadingNeura || !pergunta.trim() || usoNeura >= LIMITE}
                  style={{ background: "rgba(255,255,255,0.25)", border: "none", borderRadius: 12, width: 40, cursor: "pointer", color: "#fff", fontSize: "1rem" }}>
                  {loadingNeura ? "⏳" : "➤"}
                </button>
              </div>
              {resposta && <div style={{ marginTop: 12, background: "rgba(255,255,255,0.12)", borderRadius: 12, padding: 12, fontSize: "0.84rem", lineHeight: 1.6 }}>{resposta}</div>}
            </div>
          </aside>
        )}
      </div>
    </div>
  );
};

// ─── APP ROOT ─────────────────────────────────────────────────────────────────
export default function App() {
  const [user, setUser] = useState(null);
  const [perfil, setPerfil] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState("dashboard");
  const mobile = useIsMobile();

  useEffect(() => {
    const unsub = ouvirAuth(async (u) => {
      if (u) {
        setUser(u);
        const p = await buscarPerfil(u.uid);
        if (!p) {
          // Novo usuário — wizard vai completar o cadastro
          setPerfil(null);
        } else {
          setPerfil(p);
        }
      } else {
        setUser(null); setPerfil(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  if (loading) return (
    <div style={{ minHeight: "100vh", background: `linear-gradient(135deg, ${T.roxo}, #8B5CF6)`, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center", color: "#fff" }}>
        <div style={{ fontSize: "3rem", marginBottom: 12 }}>💜</div>
        <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800, fontSize: "2rem", marginBottom: 8 }}>NeurUp</div>
        <div style={{ fontSize: "0.9rem", opacity: 0.8 }}>Carregando...</div>
      </div>
    </div>
  );

  if (!user) return <LoginPage />;

  // Usuário logado mas cadastro incompleto → mostra wizard
  if (!perfil?.cadastroCompleto) {
    return <WizardCadastro user={user} onConcluir={async () => {
      const p = await buscarPerfil(user.uid);
      setPerfil(p);
    }} />;
  }

  const renderPage = () => {
    switch (page) {
      case "dashboard": return <DashboardPage user={user} perfil={perfil} onNav={setPage} />;
      case "comunidades": return <ComunidadesPage user={user} perfil={perfil} />;
      case "amigos": return <AmigosPage />;
      case "recados": return <RecadosPage user={user} />;
      case "perfil": return <PerfilPage user={user} perfil={perfil} />;
      case "kids": return <KidsPage user={user} perfil={perfil} />;
      default: return <DashboardPage user={user} perfil={perfil} onNav={setPage} />;
    }
  };

  return (
    <div style={{ fontFamily: "Poppins, sans-serif", background: T.bg, minHeight: "100vh" }}>
      <Header user={user} onNav={setPage} page={page} onLogout={logout} kidsMode={page === "kids"} />
      {renderPage()}
      {mobile && page !== "kids" && <BottomNav page={page} onNav={setPage} />}
      {page !== "kids" && user && perfil?.cadastroCompleto && (
        <NeuraFlutuante user={user} perfil={perfil} />
      )}
    </div>
  );
}

// ─── WIZARD DE CADASTRO ───────────────────────────────────────────────────────
const PERFIS = [
  { id: "mae", emoji: "👩", label: "Mãe", tipo: "responsavel" },
  { id: "pai", emoji: "👨", label: "Pai", tipo: "responsavel" },
  { id: "avo", emoji: "👵", label: "Avó / Avô", tipo: "responsavel" },
  { id: "professor", emoji: "👩‍🏫", label: "Professor(a)", tipo: "educador" },
  { id: "especialista", emoji: "👩‍⚕️", label: "Especialista", tipo: "especialista" },
  { id: "crianca", emoji: "👧", label: "Criança / Adolescente", tipo: "dependente_autonomo" },
];

const gerarCodigo = () => Math.random().toString(36).substring(2, 8).toUpperCase();

const WizardCadastro = ({ user, onConcluir }) => {
  const [passo, setPasso] = useState(1);
  const [perfil, setPerfil] = useState(null);
  const [form, setForm] = useState({
    nomeExibicao: "",
    dataNascimento: "",
    especialidade: "",
    instituicao: "",
    codigoFamiliar: "",
  });
  const [filho, setFilho] = useState({
    nome: "",
    nomeExibicao: "",
    dataNascimento: "",
    avatar: "⭐",
  });
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const mobile = useIsMobile();

  const AVATARES = ["⭐","🚀","🦋","🐯","🦄","🐉","🌈","🎨","⚡","🎯","🌟","🦁"];

  const concluir = async () => {
    if (!form.nomeExibicao.trim()) { setErro("Escolha como quer ser chamado na rede."); return; }
    setLoading(true); setErro("");
    try {
      const codigoFamiliar = gerarCodigo();
      const dadosBase = {
        nomeCompleto: user.displayName || "",
        nomeExibicao: form.nomeExibicao,
        email: user.email,
        foto: user.photoURL || "",
        tipo: perfil.tipo,
        persona: perfil.label,
        dataNascimento: form.dataNascimento,
        cadastroCompleto: true,
        comunidades: [],
        data: new Date().toISOString(),
      };

      if (perfil.tipo === "responsavel" || perfil.tipo === "educador") {
        await salvarPerfil(user.uid, {
          ...dadosBase,
          codigoFamiliar,
          filho: perfil.tipo === "responsavel" ? {
            nome: filho.nomeExibicao || filho.nome,
            nomeCompleto: filho.nome,
            avatar: filho.avatar,
            dataNascimento: filho.dataNascimento,
          } : null,
        });
      } else if (perfil.tipo === "especialista") {
        await salvarPerfil(user.uid, {
          ...dadosBase,
          especialidade: form.especialidade,
          instituicao: form.instituicao,
          verificado: false,
        });
      } else if (perfil.tipo === "dependente_autonomo") {
        // Busca o responsável pelo código familiar
        const { collection: col, query: q, where: w, getDocs } = await import("firebase/firestore");
        // Usa buscarPorCodigo
        await salvarPerfil(user.uid, {
          ...dadosBase,
          codigoFamiliarUsado: form.codigoFamiliar,
          pai_uid_referencia: form.codigoFamiliar, // será atualizado após vinculação
        });
      }
      onConcluir();
    } catch (e) {
      setErro("Erro ao salvar. Tente novamente.");
      console.error(e);
    }
    setLoading(false);
  };

  const containerStyle = {
    minHeight: "100vh",
    background: `linear-gradient(135deg, ${T.roxo} 0%, #8B5CF6 50%, ${T.laranja} 100%)`,
    display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
  };

  const cardStyle = {
    background: T.white, borderRadius: 36, padding: mobile ? "32px 24px" : "44px 48px",
    width: "100%", maxWidth: 520,
    boxShadow: "0 32px 80px rgba(0,0,0,0.18)",
  };

  // Indicador de passos
  const Passos = ({ total, atual }) => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 28 }}>
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} style={{
          width: i + 1 === atual ? 28 : 10, height: 10, borderRadius: 20,
          background: i + 1 <= atual ? T.roxo : T.gray100,
          transition: "all 0.3s",
        }} />
      ))}
    </div>
  );

  // ── PASSO 1: Escolha o perfil ──────────────────────────────────────────────
  if (passo === 1) return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ fontSize: "2.5rem", marginBottom: 8 }}>💜</div>
          <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800, fontSize: "1.6rem", color: T.roxo }}>
            Bem-vinda à NeurUp!
          </div>
          <p style={{ color: T.gray500, fontSize: "0.88rem", marginTop: 6 }}>
            Olá, {user.displayName?.split(" ")[0] || ""}! Quem é você na história desta família?
          </p>
        </div>
        <Passos total={3} atual={1} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {PERFIS.map(p => (
            <button key={p.id} onClick={() => setPerfil(p)} style={{
              padding: "16px 12px", borderRadius: 16, cursor: "pointer", fontFamily: "inherit",
              border: `2px solid ${perfil?.id === p.id ? T.roxo : T.gray100}`,
              background: perfil?.id === p.id ? T.roxoLight : "#fff",
              display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
              transition: "all 0.2s",
            }}>
              <span style={{ fontSize: "1.8rem" }}>{p.emoji}</span>
              <span style={{ fontWeight: 700, color: perfil?.id === p.id ? T.roxo : T.gray700, fontSize: "0.85rem" }}>{p.label}</span>
            </button>
          ))}
        </div>
        <Btn onClick={() => { if (perfil) setPasso(2); }} disabled={!perfil}
          style={{ width: "100%", marginTop: 20, padding: "14px", fontSize: "1rem" }}>
          Continuar →
        </Btn>
      </div>
    </div>
  );

  // ── PASSO 2: Nome de exibição e dados ─────────────────────────────────────
  if (passo === 2) return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ fontSize: "2.5rem", marginBottom: 8 }}>{perfil.emoji}</div>
          <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800, fontSize: "1.4rem", color: T.roxo }}>
            Como quer ser chamada?
          </div>
          <p style={{ color: T.gray500, fontSize: "0.85rem", marginTop: 6, lineHeight: 1.5 }}>
            Na NeurUp preservamos sua privacidade — seu nome completo nunca aparece para outros membros.
          </p>
        </div>
        <Passos total={3} atual={2} />

        {erro && <div style={{ background: "#FEE2E2", color: T.red, padding: "10px 14px", borderRadius: 12, marginBottom: 16, fontSize: "0.85rem" }}>{erro}</div>}

        <label style={{ fontSize: "0.78rem", fontWeight: 700, color: T.gray500, display: "block", marginBottom: 6 }}>
          NOME DE EXIBIÇÃO NA REDE
        </label>
        <Input value={form.nomeExibicao} onChange={e => setForm(f => ({ ...f, nomeExibicao: e.target.value }))}
          placeholder={perfil.id === "mae" ? "Ex: Mãe da Manu" : perfil.id === "professor" ? "Ex: Prof. Carlos" : perfil.id === "especialista" ? "Ex: Dra. Ana — Psicopedagoga" : "Ex: Avó Coruja"}
          style={{ marginBottom: 16 }} />

        <label style={{ fontSize: "0.78rem", fontWeight: 700, color: T.gray500, display: "block", marginBottom: 6 }}>
          DATA DE NASCIMENTO <span style={{ color: T.gray300, fontWeight: 400 }}>(privado — exigência legal)</span>
        </label>
        <Input type="date" value={form.dataNascimento} onChange={e => setForm(f => ({ ...f, dataNascimento: e.target.value }))}
          style={{ marginBottom: 16 }} />

        {perfil.tipo === "especialista" && (
          <>
            <label style={{ fontSize: "0.78rem", fontWeight: 700, color: T.gray500, display: "block", marginBottom: 6 }}>ESPECIALIDADE</label>
            <Input value={form.especialidade} onChange={e => setForm(f => ({ ...f, especialidade: e.target.value }))}
              placeholder="Ex: Psicopedagogia, Fonoaudiologia..." style={{ marginBottom: 16 }} />
            <label style={{ fontSize: "0.78rem", fontWeight: 700, color: T.gray500, display: "block", marginBottom: 6 }}>INSTITUIÇÃO / CONSULTÓRIO</label>
            <Input value={form.instituicao} onChange={e => setForm(f => ({ ...f, instituicao: e.target.value }))}
              placeholder="Ex: Clínica Bem Estar" style={{ marginBottom: 16 }} />
          </>
        )}

        {perfil.tipo === "educador" && (
          <>
            <label style={{ fontSize: "0.78rem", fontWeight: 700, color: T.gray500, display: "block", marginBottom: 6 }}>ESCOLA / INSTITUIÇÃO</label>
            <Input value={form.instituicao} onChange={e => setForm(f => ({ ...f, instituicao: e.target.value }))}
              placeholder="Ex: Escola Municipal João Paulo" style={{ marginBottom: 16 }} />
          </>
        )}

        {perfil.tipo === "dependente_autonomo" && (
          <>
            <label style={{ fontSize: "0.78rem", fontWeight: 700, color: T.gray500, display: "block", marginBottom: 6 }}>
              CÓDIGO FAMILIAR <span style={{ color: T.gray300, fontWeight: 400 }}>(peça ao seu responsável)</span>
            </label>
            <Input value={form.codigoFamiliar} onChange={e => setForm(f => ({ ...f, codigoFamiliar: e.target.value.toUpperCase() }))}
              placeholder="Ex: ABC123" style={{ marginBottom: 16, fontFamily: "monospace", letterSpacing: 4, fontSize: "1.1rem" }} />
          </>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
          <Btn variant="ghost" onClick={() => setPasso(1)} style={{ flex: 1 }}>← Voltar</Btn>
          <Btn onClick={() => {
            if (!form.nomeExibicao.trim()) { setErro("Escolha como quer ser chamada na rede."); return; }
            setErro("");
            if (perfil.tipo === "responsavel") { setPasso(3); } else { concluir(); }
          }} style={{ flex: 2 }} disabled={loading}>
            {perfil.tipo === "responsavel" ? "Continuar →" : loading ? "Salvando..." : "Entrar na NeurUp 💜"}
          </Btn>
        </div>
      </div>
    </div>
  );

  // ── PASSO 3: Cadastro do dependente (só para responsáveis) ────────────────
  if (passo === 3) return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ fontSize: "2.5rem", marginBottom: 8 }}>👧</div>
          <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800, fontSize: "1.4rem", color: T.roxo }}>
            Agora me conta sobre seu filho
          </div>
          <p style={{ color: T.gray500, fontSize: "0.85rem", marginTop: 6, lineHeight: 1.5 }}>
            Essas informações ajudam a personalizar a experiência na NeurUp.
          </p>
        </div>
        <Passos total={3} atual={3} />

        {erro && <div style={{ background: "#FEE2E2", color: T.red, padding: "10px 14px", borderRadius: 12, marginBottom: 16, fontSize: "0.85rem" }}>{erro}</div>}

        <label style={{ fontSize: "0.78rem", fontWeight: 700, color: T.gray500, display: "block", marginBottom: 6 }}>
          NOME COMPLETO <span style={{ color: T.gray300, fontWeight: 400 }}>(privado)</span>
        </label>
        <Input value={filho.nome} onChange={e => setFilho(f => ({ ...f, nome: e.target.value }))}
          placeholder="Nome completo da criança" style={{ marginBottom: 12 }} />

        <label style={{ fontSize: "0.78rem", fontWeight: 700, color: T.gray500, display: "block", marginBottom: 6 }}>
          COMO APARECE NA REDE
        </label>
        <Input value={filho.nomeExibicao} onChange={e => setFilho(f => ({ ...f, nomeExibicao: e.target.value }))}
          placeholder="Ex: Manu, Leo, Pequeno Explorador..." style={{ marginBottom: 12 }} />

        <label style={{ fontSize: "0.78rem", fontWeight: 700, color: T.gray500, display: "block", marginBottom: 6 }}>
          DATA DE NASCIMENTO
        </label>
        <Input type="date" value={filho.dataNascimento} onChange={e => setFilho(f => ({ ...f, dataNascimento: e.target.value }))}
          style={{ marginBottom: 16 }} />

        <label style={{ fontSize: "0.78rem", fontWeight: 700, color: T.gray500, display: "block", marginBottom: 10 }}>
          AVATAR
        </label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 20 }}>
          {AVATARES.map(a => (
            <button key={a} onClick={() => setFilho(f => ({ ...f, avatar: a }))} style={{
              fontSize: "1.6rem", padding: "8px 10px", borderRadius: 12, cursor: "pointer",
              border: `2px solid ${filho.avatar === a ? T.roxo : T.gray100}`,
              background: filho.avatar === a ? T.roxoLight : "#fff",
            }}>{a}</button>
          ))}
        </div>

        <div style={{ background: T.laranjaLight, border: `1px dashed ${T.laranja}`, borderRadius: 14, padding: 14, marginBottom: 20 }}>
          <p style={{ margin: 0, fontSize: "0.82rem", color: T.gray700, lineHeight: 1.6 }}>
            💡 Após o cadastro você receberá um <strong>Código Familiar</strong> para compartilhar com seu filho. Ele usará esse código para criar a própria conta e se vincular a você.
          </p>
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <Btn variant="ghost" onClick={() => setPasso(2)} style={{ flex: 1 }}>← Voltar</Btn>
          <Btn onClick={concluir} style={{ flex: 2 }} disabled={loading || !filho.nome.trim()}>
            {loading ? "Salvando..." : "Entrar na NeurUp 💜"}
          </Btn>
        </div>
      </div>
    </div>
  );

  return null;
};